import {
    Calculate as CalculateIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { enqueueSnackbar } from "notistack";
import axios from "axios";
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../AppContext";
import {
    useCloseShiftEntry,
    useGetExchangeRateAdjustments,
    useGetClosingInvoicesData,
    useGetPosEntryDetails,
    useSearchPOSOpening,
    useValidateLink,
} from "../../shared/hooks/usePOSData";
import { getCurrentDate } from "../../utils/dateUtils";

function toNumber(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

const ILS_DENOMINATIONS = [0.5, 1, 2, 5, 10, 20, 50, 100, 200];
const EMPTY_EXCHANGE_RATE_ADJUSTMENTS = {};

function buildIlsDenominationCounts() {
    return ILS_DENOMINATIONS.reduce((acc, d) => {
        acc[String(d)] = "";
        return acc;
    }, {});
}

function areMapsEqual(a = {}, b = {}) {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) => a[key] === b[key]);
}

function isEnabled(value) {
    return value === 1 || value === "1" || value === true;
}

function normalizePaymentModeForClosing(mode) {
    const normalized = String(mode || "")
        .trim()
        .toLowerCase();
    if (!normalized) return "Cash";
    if (normalized.includes("visa") || normalized.includes("credit")) {
        return "Credit Card";
    }
    return "Cash";
}

function aggregateRowsForBackend(rows) {
    const grouped = new Map();
    (rows || []).forEach((row) => {
        const mode = normalizePaymentModeForClosing(row?.mode_of_payment);
        const existing = grouped.get(mode) || {
            mode_of_payment: mode,
            opening_amount: 0,
            expected_amount: 0,
            closing_amount: 0,
            difference: 0,
        };
        existing.opening_amount += toNumber(row?.opening_amount);
        existing.expected_amount += toNumber(row?.expected_amount);
        existing.closing_amount += toNumber(row?.closing_amount);
        existing.difference =
            existing.closing_amount - existing.expected_amount;
        grouped.set(mode, existing);
    });
    return Array.from(grouped.values());
}

function applyDenominationOverridesToBackendRows(
    rows,
    denominationTotals,
    { overrideCashWithDenominations = false, overrideCardLike = false } = {}
) {
    const byMode = new Map(
        (rows || []).map((r) => [r.mode_of_payment, { ...r }])
    );

    const ensureRow = (mode) => {
        if (!byMode.has(mode)) {
            byMode.set(mode, {
                mode_of_payment: mode,
                opening_amount: 0,
                expected_amount: 0,
                closing_amount: 0,
                difference: 0,
            });
        }
        return byMode.get(mode);
    };

    const cashRow = ensureRow("Cash");
    cashRow.closing_amount =
        (overrideCashWithDenominations
            ? toNumber(denominationTotals?.ils)
            : toNumber(cashRow.closing_amount)) +
        toNumber(denominationTotals?.foreignInCompanyCurrency);
    cashRow.difference =
        cashRow.closing_amount - toNumber(cashRow.expected_amount);

    if (overrideCardLike) {
        const creditCardRow = ensureRow("Credit Card");
        creditCardRow.closing_amount = toNumber(denominationTotals?.visa);
        creditCardRow.difference =
            creditCardRow.closing_amount -
            toNumber(creditCardRow.expected_amount);
    }

    return Array.from(byMode.values());
}

function resolveForeignCurrencyForMode(mode, exchangeRateAdjustments) {
    const normalizedMode = String(mode || "")
        .trim()
        .toUpperCase();
    const currencies = Object.keys(exchangeRateAdjustments || {});
    for (const currency of currencies) {
        const c = String(currency || "")
            .trim()
            .toUpperCase();
        if (!c) continue;
        if (normalizedMode === c || normalizedMode.endsWith(` ${c}`)) {
            return c;
        }
    }
    return null;
}

function inferCurrencyFromModeName(mode) {
    const tokens = String(mode || "")
        .trim()
        .toUpperCase()
        .split(/[^A-Z]+/)
        .filter(Boolean);

    const stopWords = new Set([
        "CASH",
        "CREDIT",
        "CARD",
        "DEBIT",
        "PAYMENT",
        "MODE",
        "OF",
        "POS",
    ]);

    for (let i = tokens.length - 1; i >= 0; i -= 1) {
        const token = tokens[i];
        const aliases = { JD: "JOD", NIS: "ILS" };
        if (aliases[token]) {
            return aliases[token];
        }
        if (token.length === 3 && !stopWords.has(token)) {
            return token;
        }
    }
    return null;
}

function buildPaymentReconciliationRows({
    openingEntry,
    invoices,
    prevClosingByMode,
    initializeClosingAmounts,
}) {
    const rowsByMode = new Map();

    // Equivalent to: set_opening_amounts(frm)
    (openingEntry?.balance_details || []).forEach((detail) => {
        const mode = detail?.mode_of_payment;
        if (!mode) return;

        const opening = toNumber(detail?.opening_amount);
        const existing = rowsByMode.get(mode) || {
            mode_of_payment: mode,
            opening_amount: 0,
            expected_amount: 0,
            closing_amount: 0,
            difference: 0,
        };

        existing.opening_amount += opening;
        existing.expected_amount += opening;

        rowsByMode.set(mode, existing);
    });

    // Equivalent to: refresh_payments(d, frm, is_new)
    (invoices || []).forEach((invoice) => {
        const payments = invoice?.payments || [];
        payments.forEach((p) => {
            const mode = p?.mode_of_payment;
            if (!mode) return;

            let amount = toNumber(p?.amount);
            if (
                p?.account &&
                invoice?.account_for_change_amount &&
                p.account === invoice.account_for_change_amount
            ) {
                amount -= toNumber(invoice?.change_amount);
            }

            const existing = rowsByMode.get(mode) || {
                mode_of_payment: mode,
                opening_amount: 0,
                expected_amount: 0,
                closing_amount: 0,
                difference: 0,
            };

            existing.expected_amount += amount;
            rowsByMode.set(mode, existing);
        });
    });

    const rows = Array.from(rowsByMode.values()).sort((a, b) =>
        a.mode_of_payment.localeCompare(b.mode_of_payment)
    );

    // Equivalent to: if (is_new) payment.closing_amount = payment.expected_amount;
    rows.forEach((r) => {
        const prevClosing = prevClosingByMode?.get(r.mode_of_payment);
        const closing = initializeClosingAmounts
            ? r.expected_amount
            : prevClosing ?? r.expected_amount;

        r.closing_amount = toNumber(closing);
        r.difference = toNumber(r.closing_amount) - toNumber(r.expected_amount);
    });

    return rows;
}

function buildTaxesRows(invoices) {
    const rowsByKey = new Map();
    (invoices || []).forEach((invoice) => {
        (invoice?.taxes || []).forEach((t) => {
            const accountHead = t?.account_head;
            const rate = toNumber(t?.rate);
            if (!accountHead) return;

            const key = `${accountHead}::${rate}`;
            const existing = rowsByKey.get(key) || {
                account_head: accountHead,
                rate,
                amount: 0,
            };

            existing.amount += toNumber(t?.tax_amount);
            rowsByKey.set(key, existing);
        });
    });

    return Array.from(rowsByKey.values()).sort((a, b) =>
        a.account_head.localeCompare(b.account_head)
    );
}

function PosCashCloseDialog({ open, onClose, onSuccess }) {
    const { posProfile, userInfo, company, isKitchenUser, restaurantSettings } =
        useContext(AppContext);
    const { t } = useTranslation();

    const showExpectedAmounts = isEnabled(
        posProfile?.show_expected_amounts_on_closing
    );
    const autoCashCloseEnabled = isEnabled(
        restaurantSettings?.docs?.[0]?.auto_cash_close ||
            restaurantSettings?.auto_cash_close
    );
    const shouldDisplayExpectedAmounts =
        showExpectedAmounts && !autoCashCloseEnabled;
    const shouldShowManualClosingInputs = !autoCashCloseEnabled;

    const closeShiftEntryMutation = useCloseShiftEntry();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const companyCurrency = useMemo(
        () => (posProfile?.currency || "ILS").toUpperCase(),
        [posProfile?.currency]
    );

    const allowedPaymentModes = useMemo(() => {
        const modes =
            posProfile?.payments
                ?.map((p) => p?.mode_of_payment)
                ?.filter(Boolean) || [];
        return Array.from(new Set(modes));
    }, [posProfile?.payments]);

    const [methodCurrencyMap, setMethodCurrencyMap] = useState({});
    const [fallbackExchangeRates, setFallbackExchangeRates] = useState({});

    const cashDenominationOnClosingEnabled = useMemo(() => {
        return isEnabled(posProfile?.cash_denomination_on_closing);
    }, [posProfile?.cash_denomination_on_closing]);
    const shouldShowDenominationInputs =
        shouldShowManualClosingInputs && cashDenominationOnClosingEnabled;
    const shouldShowPaymentReconciliation = shouldShowManualClosingInputs;

    useEffect(() => {
        let cancelled = false;
        const company = posProfile?.company;
        if (!company || !allowedPaymentModes.length) return;

        const fetchCurrencyForMethod = async (modeOfPayment) => {
            try {
                const { data } = await axios.get(
                    "method/restaurant_management.api.get_mode_of_payment_account_currency",
                    {
                        params: {
                            mode_of_payment: modeOfPayment,
                            company,
                        },
                    }
                );
                return data?.message || null;
            } catch (e) {
                return null;
            }
        };

        (async () => {
            const updates = {};
            for (const mode of allowedPaymentModes) {
                const resolved = await fetchCurrencyForMethod(mode);
                updates[mode] = resolved ? resolved.toUpperCase() : null;
            }
            if (!cancelled) {
                setMethodCurrencyMap((prev) => ({ ...prev, ...updates }));
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [allowedPaymentModes, posProfile?.company, companyCurrency]);

    const [selectedOpeningEntryName, setSelectedOpeningEntryName] =
        useState(null);
    const [periodEndDate, setPeriodEndDate] = useState(() => getCurrentDate());

    // Equivalent to Desk: link field options come from "check_opening_entry"
    const { data: openingCandidatesRaw, isLoading: loadingOpeningCandidates } =
        useSearchPOSOpening({ user: userInfo?.name });

    const openingCandidates = useMemo(() => {
        const list = openingCandidatesRaw || [];
        return list.filter((o) => {
            // Most backends return at least {name, company, pos_profile, user, period_start_date, status}
            const companyMatch = company?.name
                ? o.company === company.name ||
                  o.company === company.company_name
                : true;
            const posProfileMatch = posProfile?.name
                ? o.pos_profile === posProfile.name ||
                  o.pos_profile === posProfile?.name
                : true;
            return companyMatch && posProfileMatch;
        });
    }, [
        openingCandidatesRaw,
        company?.name,
        company?.company_name,
        posProfile?.name,
    ]);

    useEffect(() => {
        if (!selectedOpeningEntryName) return;
        const stillValid = (openingCandidates || []).some(
            (entry) => entry?.name === selectedOpeningEntryName
        );
        if (!stillValid) {
            setSelectedOpeningEntryName(null);
        }
    }, [openingCandidates, selectedOpeningEntryName]);

    useEffect(() => {
        if (!open) return;
        setPeriodEndDate(getCurrentDate());
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (isKitchenUser) return;
        if (!openingCandidates?.length) return;
        if (selectedOpeningEntryName) return;
        if (openingCandidates.length === 1) {
            setSelectedOpeningEntryName(openingCandidates[0]?.name || null);
        }
    }, [open, isKitchenUser, openingCandidates, selectedOpeningEntryName]);

    // Lightweight validate_link (Desk does this when a Link value is set)
    const { data: validatedOpeningLink, isLoading: loadingValidatedLink } =
        useValidateLink("POS Opening Entry", selectedOpeningEntryName, [
            "period_start_date",
            "pos_profile",
            "user",
        ]);

    // Full document load (to get balance_details)
    const { data: selectedOpeningEntry, isLoading: loadingOpeningDoc } =
        useGetPosEntryDetails(selectedOpeningEntryName);

    const closingInvoicesParams = useMemo(
        () => ({
            startDate:
                validatedOpeningLink?.period_start_date ||
                selectedOpeningEntry?.period_start_date,
            endDate: periodEndDate,
            posProfile: posProfile?.name,
            user: userInfo?.name,
        }),
        [
            validatedOpeningLink?.period_start_date,
            selectedOpeningEntry?.period_start_date,
            periodEndDate,
            posProfile?.name,
            userInfo?.name,
        ]
    );

    const { data: posProfileClosingEntries, isLoading: loadingInvoices } =
        useGetClosingInvoicesData(
            closingInvoicesParams.startDate,
            closingInvoicesParams.endDate,
            closingInvoicesParams.posProfile,
            closingInvoicesParams.user,
            undefined, // onSuccess
            undefined // onError
        );

    const openingStartDate =
        validatedOpeningLink?.period_start_date ||
        selectedOpeningEntry?.period_start_date;
    const { data: exchangeRateAdjustmentsData } = useGetExchangeRateAdjustments(
        openingStartDate,
        periodEndDate,
        company?.name || company?.company_name,
        posProfile?.currency || "ILS"
    );
    const exchangeRateAdjustments =
        exchangeRateAdjustmentsData || EMPTY_EXCHANGE_RATE_ADJUSTMENTS;

    const hasSelectedOpeningEntry = !!selectedOpeningEntryName;
    const shouldLoadClosingInvoices =
        !!closingInvoicesParams.startDate &&
        !!closingInvoicesParams.endDate &&
        !!closingInvoicesParams.posProfile &&
        !!closingInvoicesParams.user;
    const isDialogLoading =
        loadingOpeningCandidates ||
        (hasSelectedOpeningEntry &&
            (loadingValidatedLink || loadingOpeningDoc)) ||
        (shouldLoadClosingInvoices && loadingInvoices);

    const totals = useMemo(() => {
        if (!posProfileClosingEntries)
            return { grandTotal: 0, netTotal: 0, totalQuantity: 0 };

        let grandTotal = 0;
        let netTotal = 0;
        let totalQuantity = 0;

        posProfileClosingEntries.forEach((invoice) => {
            grandTotal += invoice.grand_total || 0;
            netTotal += invoice.net_total || 0;
            totalQuantity += invoice.total_qty || 0;
        });

        return { grandTotal, netTotal, totalQuantity };
    }, [posProfileClosingEntries]);

    const [paymentReconciliation, setPaymentReconciliation] = useState([]);

    // Equivalent to: reset_values(frm) + set_form_data(data, frm)
    useEffect(() => {
        if (!open) return;
        if (!selectedOpeningEntry) return;
        // Wait until opening entry includes balance_details; otherwise we may build rows without opening_amount
        if (!Array.isArray(selectedOpeningEntry?.balance_details)) return;

        const prevClosingByMode = new Map(
            (paymentReconciliation || []).map((r) => [
                r.mode_of_payment,
                r.closing_amount,
            ])
        );

        const initializeClosingAmounts = paymentReconciliation.length === 0;

        const rows = buildPaymentReconciliationRows({
            openingEntry: selectedOpeningEntry,
            invoices: posProfileClosingEntries || [],
            prevClosingByMode,
            initializeClosingAmounts,
        });

        setPaymentReconciliation(rows);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        open,
        selectedOpeningEntry?.name,
        selectedOpeningEntry?.balance_details?.length,
        posProfileClosingEntries,
    ]);

    const taxes = useMemo(() => {
        return buildTaxesRows(posProfileClosingEntries || []);
    }, [posProfileClosingEntries]);

    const handleClosingAmountChange = useCallback((mode, value) => {
        setPaymentReconciliation((prev) =>
            (prev || []).map((r) => {
                if (r.mode_of_payment !== mode) return r;
                const closing = toNumber(value);
                const expected = toNumber(r.expected_amount);
                return {
                    ...r,
                    closing_amount: closing,
                    difference: closing - expected,
                };
            })
        );
    }, []);

    const getExchangeRateDetails = useCallback(
        (currency) => {
            const c = String(currency || "").toUpperCase();
            if (!c || c === companyCurrency) {
                return { exchange_rate: 1, rate_source: "company_currency" };
            }
            const primary = toNumber(
                exchangeRateAdjustments?.[c]?.exchange_rate
            );
            if (primary > 0) {
                return {
                    exchange_rate: primary,
                    rate_source:
                        exchangeRateAdjustments?.[c]?.rate_source ||
                        "journal_entry_weighted_rate",
                    journal_entries:
                        exchangeRateAdjustments?.[c]?.journal_entries || [],
                };
            }
            const fallback = toNumber(fallbackExchangeRates?.[c]);
            if (fallback > 0) {
                return {
                    exchange_rate: fallback,
                    rate_source: "live_exchange_rate_fallback",
                };
            }
            return { exchange_rate: 0, rate_source: "missing_rate" };
        },
        [companyCurrency, exchangeRateAdjustments, fallbackExchangeRates]
    );

    const getExchangeRateForCurrency = useCallback(
        (currency) => getExchangeRateDetails(currency).exchange_rate,
        [getExchangeRateDetails]
    );

    const toDisplayAmount = useCallback(
        (mode, amount) => {
            if (!shouldShowDenominationInputs) return toNumber(amount);
            const currency = resolveForeignCurrencyForMode(
                mode,
                exchangeRateAdjustments
            );
            if (!currency) return toNumber(amount);
            const rate = toNumber(getExchangeRateForCurrency(currency));
            if (rate <= 0) return toNumber(amount);
            return toNumber(amount) / rate;
        },
        [
            shouldShowDenominationInputs,
            exchangeRateAdjustments,
            getExchangeRateForCurrency,
        ]
    );
    const toDisplayAmountInput = useCallback(
        (mode, amount) => Number(toDisplayAmount(mode, amount).toFixed(2)),
        [toDisplayAmount]
    );

    const allowedCardLikeModes = useMemo(() => {
        return allowedPaymentModes.filter(
            (mode) => normalizePaymentModeForClosing(mode) === "Credit Card"
        );
    }, [allowedPaymentModes]);

    const allowedForeignCurrencies = useMemo(() => {
        const currencies = new Set();
        allowedPaymentModes.forEach((mode) => {
            if (normalizePaymentModeForClosing(mode) !== "Cash") return;
            const detectedCurrency =
                methodCurrencyMap[mode] || inferCurrencyFromModeName(mode);
            const currency = (
                detectedCurrency || companyCurrency
            ).toUpperCase();
            if (currency && currency !== companyCurrency) {
                currencies.add(currency);
            }
        });
        return Array.from(currencies);
    }, [allowedPaymentModes, methodCurrencyMap, companyCurrency]);

    const [closingDenomination, setClosingDenomination] = useState(() => ({
        cardLikeByMode: {},
        foreign: {},
        ils: buildIlsDenominationCounts(),
    }));

    useEffect(() => {
        setClosingDenomination((prev) => {
            const prevState = prev || {};
            const nextCardLikeByMode = {};
            allowedCardLikeModes.forEach((mode) => {
                nextCardLikeByMode[mode] =
                    prevState?.cardLikeByMode?.[mode] ?? "";
            });

            const mergedForeignCurrencies = Array.from(
                new Set([
                    ...allowedForeignCurrencies,
                    ...Object.keys(exchangeRateAdjustments || {}),
                    ...Object.keys(prevState?.foreign || {}),
                ])
            );
            const nextForeign = {};
            mergedForeignCurrencies.forEach((currency) => {
                nextForeign[currency] = prevState?.foreign?.[currency] ?? "";
            });

            const nextIls = {};
            ILS_DENOMINATIONS.forEach((d) => {
                nextIls[String(d)] = prevState?.ils?.[String(d)] ?? "";
            });

            const nextState = {
                cardLikeByMode: nextCardLikeByMode,
                foreign: nextForeign,
                ils: nextIls,
            };

            if (
                areMapsEqual(prevState.cardLikeByMode, nextCardLikeByMode) &&
                areMapsEqual(prevState.foreign, nextForeign) &&
                areMapsEqual(prevState.ils, nextIls)
            ) {
                return prevState;
            }

            return nextState;
        });
    }, [
        allowedCardLikeModes,
        allowedForeignCurrencies,
        exchangeRateAdjustments,
    ]);

    const foreignCurrencyFields = useMemo(() => {
        const fromAllowedModes = allowedForeignCurrencies;
        const fromAdjustments = Object.keys(exchangeRateAdjustments || {});
        const fromInput = Object.keys(closingDenomination?.foreign || {});
        return Array.from(
            new Set([...fromAllowedModes, ...fromAdjustments, ...fromInput])
        );
    }, [
        allowedForeignCurrencies,
        exchangeRateAdjustments,
        closingDenomination?.foreign,
    ]);

    const shouldShowForeignCurrencyInputs =
        shouldShowManualClosingInputs && foreignCurrencyFields.length > 0;
    const shouldShowClosingCountInputs =
        shouldShowDenominationInputs || shouldShowForeignCurrencyInputs;

    useEffect(() => {
        let cancelled = false;
        const missingCurrencies = foreignCurrencyFields.filter((currency) => {
            const c = String(currency || "").toUpperCase();
            if (!c || c === companyCurrency) return false;
            const primary = toNumber(
                exchangeRateAdjustments?.[c]?.exchange_rate
            );
            const fallback = toNumber(fallbackExchangeRates?.[c]);
            return primary <= 0 && fallback <= 0;
        });
        if (!missingCurrencies.length) return;

        (async () => {
            const updates = {};
            for (const fromCurrency of missingCurrencies) {
                try {
                    const res = await fetch(
                        `https://api.exchangerate-api.com/v4/latest/${fromCurrency}`
                    );
                    const json = await res.json();
                    const rate = Number(json?.rates?.[companyCurrency]);
                    if (rate > 0) {
                        updates[fromCurrency] = rate;
                    }
                } catch (e) {
                    // ignore and keep waiting for adjustment entries
                }
            }
            if (!cancelled && Object.keys(updates).length) {
                setFallbackExchangeRates((prev) => ({ ...prev, ...updates }));
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        foreignCurrencyFields,
        companyCurrency,
        exchangeRateAdjustments,
        fallbackExchangeRates,
    ]);

    // Derived totals from denomination inputs (empty -> 0)
    const denominationTotals = useMemo(() => {
        const visa = Object.values(
            closingDenomination?.cardLikeByMode || {}
        ).reduce((sum, entered) => sum + toNumber(entered), 0);
        const foreign = closingDenomination?.foreign || {};

        const ilsCounts = closingDenomination?.ils || {};
        let ils = 0;
        ILS_DENOMINATIONS.forEach((d) => {
            const count = toNumber(ilsCounts[String(d)]);
            ils += d * count;
        });

        const foreignDetails = Object.entries(foreign).reduce(
            (acc, [currency, entered]) => {
                const amount = toNumber(entered);
                const rateDetails = getExchangeRateDetails(currency);
                const rate = toNumber(rateDetails.exchange_rate);
                acc[currency] = {
                    currency,
                    amount,
                    exchange_rate: rate,
                    rate_source: rateDetails.rate_source,
                    journal_entries: rateDetails.journal_entries || [],
                    amount_in_company_currency: Number(
                        (amount * rate).toFixed(2)
                    ),
                };
                return acc;
            },
            {}
        );
        const foreignInCompanyCurrency = Object.values(foreignDetails).reduce(
            (sum, item) => sum + toNumber(item?.amount_in_company_currency),
            0
        );

        return { visa, ils, foreignDetails, foreignInCompanyCurrency };
    }, [
        closingDenomination,
        getExchangeRateDetails,
    ]);

    const cashTransactinosPayload = useMemo(() => {
        if (!shouldShowClosingCountInputs) return undefined;
        return {
            version: 1,
            type: "cash_denomination_on_closing",
            company_currency: companyCurrency,
            ils_denomination_enabled: shouldShowDenominationInputs,
            denomination: {
                visa: denominationTotals.visa,
                foreign: closingDenomination?.foreign || {},
                ils: shouldShowDenominationInputs
                    ? closingDenomination?.ils || {}
                    : {},
                card_like_by_mode: closingDenomination?.cardLikeByMode || {},
            },
            totals: {
                visa: denominationTotals.visa,
                ils: denominationTotals.ils,
                foreign_in_company_currency:
                    denominationTotals.foreignInCompanyCurrency,
                cash_in_company_currency:
                    toNumber(denominationTotals.ils) +
                    toNumber(denominationTotals.foreignInCompanyCurrency),
            },
            foreign_details: denominationTotals.foreignDetails,
        };
    }, [
        shouldShowClosingCountInputs,
        shouldShowDenominationInputs,
        closingDenomination,
        denominationTotals,
        companyCurrency,
    ]);

    const displayedPaymentReconciliation = useMemo(() => {
        if (!shouldShowClosingCountInputs) return paymentReconciliation || [];

        return (paymentReconciliation || []).map((row) => {
            const normalizedMode = normalizePaymentModeForClosing(
                row?.mode_of_payment
            );
            let closingAmount = toNumber(row?.closing_amount);

            if (normalizedMode === "Cash") {
                closingAmount =
                    (shouldShowDenominationInputs
                        ? toNumber(denominationTotals?.ils)
                        : toNumber(row?.closing_amount)) +
                    toNumber(denominationTotals?.foreignInCompanyCurrency);
            } else if (
                shouldShowDenominationInputs &&
                normalizedMode === "Credit Card"
            ) {
                const exactCardLikeAmount =
                    closingDenomination?.cardLikeByMode?.[
                        row?.mode_of_payment
                    ];
                closingAmount =
                    exactCardLikeAmount !== undefined
                        ? toNumber(exactCardLikeAmount)
                        : toNumber(denominationTotals?.visa);
            }

            return {
                ...row,
                closing_amount: closingAmount,
                difference: closingAmount - toNumber(row?.expected_amount),
            };
        });
    }, [
        shouldShowClosingCountInputs,
        shouldShowDenominationInputs,
        paymentReconciliation,
        denominationTotals,
        closingDenomination?.cardLikeByMode,
    ]);

    const hasDifferences = useMemo(() => {
        return (displayedPaymentReconciliation || []).some(
            (r) => Math.abs(toNumber(r.difference)) > 0.01
        );
    }, [displayedPaymentReconciliation]);

    const lockedPaymentModes = useMemo(() => {
        if (!shouldShowDenominationInputs) return new Set();
        return new Set(["Cash", "Credit Card"]);
    }, [shouldShowDenominationInputs]);

    const handleSubmit = async () => {
        if (!selectedOpeningEntry || !posProfileClosingEntries?.length) return;

        setIsSubmitting(true);
        try {
            // Equivalent to: before_save(frm) recomputation
            const prevClosingByMode = new Map(
                (paymentReconciliation || []).map((r) => [
                    r.mode_of_payment,
                    r.closing_amount,
                ])
            );

            const rowsFresh = buildPaymentReconciliationRows({
                openingEntry: selectedOpeningEntry,
                invoices: posProfileClosingEntries || [],
                prevClosingByMode,
                initializeClosingAmounts: false,
            });

            let rowsForBackend = aggregateRowsForBackend(rowsFresh);
            if (shouldShowClosingCountInputs) {
                rowsForBackend = applyDenominationOverridesToBackendRows(
                    rowsForBackend,
                    denominationTotals,
                    {
                        overrideCashWithDenominations:
                            shouldShowDenominationInputs,
                        overrideCardLike: shouldShowDenominationInputs,
                    }
                );
            }

            closeShiftEntryMutation.mutate(
                {
                    amount_in_register: rowsForBackend,
                    pos_opening_entry: selectedOpeningEntry?.name,
                    cash_transactinos: cashTransactinosPayload,
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar(t("posCashClose.submitted"), {
                            variant: "success",
                        });
                        if (onSuccess) onSuccess();
                        handleClose();
                    },
                    onError: (error) => {
                        console.error(
                            "[close_shift_entry] UI submit failed",
                            error
                        );
                        enqueueSnackbar(t("posCashClose.submitError"), {
                            variant: "error",
                        });
                    },
                    onSettled: () => {
                        setIsSubmitting(false);
                    },
                    // enqueueSnackbar("POS Closing Entry submitted", {
                    //     variant: "success",
                    // });
                    // if (onSuccess) onSuccess();
                    // handleClose();
                    // setIsSubmitting(false);
                }
            );

            // await new Promise((resolve, reject) => {
            //     saveAndSubmitMutation.mutate(
            //         { doc: closingEntryDoc },
            //         {
            //             onSuccess: resolve,
            //             onError: reject,
            //         }
            //     );
            // });

            // enqueueSnackbar("POS Closing Entry submitted", {
            //     variant: "success",
            // });
            // if (onSuccess) onSuccess();
            // handleClose();
            // setIsSubmitting(false);
        } catch (error) {
            console.error("Error creating POS Closing Entry:", error);
            enqueueSnackbar(t("posCashClose.submitError"), {
                variant: "error",
            });
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        const resetCardLikeByMode = {};
        allowedCardLikeModes.forEach((mode) => {
            resetCardLikeByMode[mode] = "";
        });
        const resetForeign = {};
        allowedForeignCurrencies.forEach((currency) => {
            resetForeign[currency] = "";
        });
        setPaymentReconciliation([]);
        setClosingDenomination({
            cardLikeByMode: resetCardLikeByMode,
            foreign: resetForeign,
            ils: buildIlsDenominationCounts(),
        });
        if (onClose) onClose();
    };

    if (isDialogLoading) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogContent>
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        py={4}
                    >
                        <CircularProgress />
                        <Typography variant="h6" sx={{ ml: 2 }}>
                            {t("common.loading")}
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <>
            {/* Main Cash Close Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography variant="h5" fontWeight="bold">
                            {t("posCashClose.title")}
                        </Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    <Grid container spacing={3}>
                        {/* POS Opening Entry */}
                        <Grid item xs={12}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        color="primary"
                                    >
                                        {t("posCashClose.openingEntryTitle")}
                                    </Typography>
                                    {openingCandidates.length > 1 ? (
                                        <Autocomplete
                                            options={openingCandidates || []}
                                            getOptionLabel={(o) =>
                                                o?.name
                                                    ? `${o.name}${
                                                          o.period_start_date
                                                              ? ` • ${o.period_start_date}`
                                                              : ""
                                                      }`
                                                    : ""
                                            }
                                            value={
                                                openingCandidates.find(
                                                    (o) =>
                                                        o.name ===
                                                        selectedOpeningEntryName
                                                ) || null
                                            }
                                            onChange={(_, v) => {
                                                setPaymentReconciliation([]);
                                                setSelectedOpeningEntryName(
                                                    v?.name || null
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t(
                                                        "posCashClose.selectOpeningEntry"
                                                    )}
                                                    size="small"
                                                />
                                            )}
                                        />
                                    ) : openingCandidates.length === 1 ? (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={t(
                                                "posCashClose.openingEntry"
                                            )}
                                            value={
                                                openingCandidates[0]?.name || ""
                                            }
                                            disabled
                                        />
                                    ) : (
                                        <Alert severity="warning">
                                            {t("posCashClose.noOpeningEntry")}
                                        </Alert>
                                    )}

                                    {(validatedOpeningLink?.period_start_date ||
                                        selectedOpeningEntry?.period_start_date) && (
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            display="block"
                                            sx={{ mt: 1 }}
                                        >
                                            {t("posCashClose.periodStart")}:{" "}
                                            {validatedOpeningLink?.period_start_date ||
                                                selectedOpeningEntry?.period_start_date}
                                            {" • "}
                                            {t("posCashClose.periodEnd")}:{" "}
                                            {periodEndDate}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Summary Section */}
                        <Grid item xs={12}>
                            <Card elevation={2}>
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        color="primary"
                                    >
                                        {t("posCashClose.transactionSummary")}
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Box textAlign="center">
                                                <Typography
                                                    variant="h4"
                                                    color="success.main"
                                                >
                                                    {posProfileClosingEntries?.length ||
                                                        0}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {t("posCashClose.invoices")}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        {shouldDisplayExpectedAmounts && (
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Box textAlign="center">
                                                    <Typography
                                                        variant="h4"
                                                        color="primary"
                                                    >
                                                        ₪
                                                        {totals.grandTotal.toFixed(
                                                            2
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        {t(
                                                            "posCashClose.totalSales"
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Box textAlign="center">
                                                <Typography
                                                    variant="h4"
                                                    color="info.main"
                                                >
                                                    {totals.totalQuantity}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {t(
                                                        "posCashClose.totalItems"
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Reconciliation Table */}
                        {shouldShowPaymentReconciliation && (
                            <Grid item xs={12}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            color="primary"
                                        >
                                            {t(
                                                "posCashClose.paymentReconciliation"
                                            )}
                                        </Typography>
                                        <TableContainer
                                            component={Paper}
                                            variant="outlined"
                                        >
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>
                                                            <strong>
                                                                {t(
                                                                    "posCashClose.paymentMethod"
                                                                )}
                                                            </strong>
                                                        </TableCell>
                                                        {shouldDisplayExpectedAmounts && (
                                                            <>
                                                                <TableCell align="right">
                                                                    <strong>
                                                                        {t(
                                                                            "posCashClose.openingAmount"
                                                                        )}
                                                                    </strong>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <strong>
                                                                        {t(
                                                                            "posCashClose.expectedAmount"
                                                                        )}
                                                                    </strong>
                                                                </TableCell>
                                                            </>
                                                        )}
                                                        <TableCell align="right">
                                                            <strong>
                                                                {t(
                                                                    "posCashClose.actualAmount"
                                                                )}
                                                            </strong>
                                                        </TableCell>
                                                        {shouldDisplayExpectedAmounts && (
                                                            <>
                                                                <TableCell align="right">
                                                                    <strong>
                                                                        {t(
                                                                            "posCashClose.differenceAmount"
                                                                        )}
                                                                    </strong>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <strong>
                                                                        {t(
                                                                            "posCashClose.status"
                                                                        )}
                                                                    </strong>
                                                                </TableCell>
                                                            </>
                                                        )}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {(
                                                        displayedPaymentReconciliation ||
                                                        []
                                                    ).map((row) => {
                                                        const isBalanced =
                                                            Math.abs(
                                                                toNumber(
                                                                    row.difference
                                                                )
                                                            ) < 0.01;
                                                        return (
                                                            <TableRow
                                                                key={
                                                                    row.mode_of_payment
                                                                }
                                                            >
                                                                <TableCell>
                                                                    {
                                                                        row.mode_of_payment
                                                                    }
                                                                </TableCell>
                                                                {shouldDisplayExpectedAmounts && (
                                                                    <>
                                                                        <TableCell align="right">
                                                                            {toNumber(
                                                                                toDisplayAmount(
                                                                                    row.mode_of_payment,
                                                                                    row.opening_amount
                                                                                )
                                                                            ).toFixed(
                                                                                2
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            {toNumber(
                                                                                toDisplayAmount(
                                                                                    row.mode_of_payment,
                                                                                    row.expected_amount
                                                                                )
                                                                            ).toFixed(
                                                                                2
                                                                            )}
                                                                        </TableCell>
                                                                    </>
                                                                )}
                                                                <TableCell align="right">
                                                                    <TextField
                                                                        type="number"
                                                                        size="small"
                                                                        value={toDisplayAmountInput(
                                                                            row.mode_of_payment,
                                                                            row.closing_amount
                                                                        )}
                                                                        disabled={
                                                                            shouldShowDenominationInputs ||
                                                                            lockedPaymentModes.has(
                                                                                row.mode_of_payment
                                                                            )
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            handleClosingAmountChange(
                                                                                row.mode_of_payment,
                                                                                e
                                                                                    .target
                                                                                    .value
                                                                            )
                                                                        }
                                                                        inputProps={{
                                                                            style: {
                                                                                textAlign:
                                                                                    "right",
                                                                            },
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                {shouldDisplayExpectedAmounts && (
                                                                    <>
                                                                        <TableCell
                                                                            align="right"
                                                                            sx={{
                                                                                color:
                                                                                    row.difference >
                                                                                    0
                                                                                        ? "success.main"
                                                                                        : row.difference <
                                                                                          0
                                                                                        ? "error.main"
                                                                                        : "text.primary",
                                                                            }}
                                                                        >
                                                                            {row.difference >
                                                                            0
                                                                                ? "+"
                                                                                : ""}
                                                                            {toNumber(
                                                                                toDisplayAmount(
                                                                                    row.mode_of_payment,
                                                                                    row.difference
                                                                                )
                                                                            ).toFixed(
                                                                                2
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell align="center">
                                                                            <Chip
                                                                                label={
                                                                                    isBalanced
                                                                                        ? t(
                                                                                              "posCashClose.balanced"
                                                                                          )
                                                                                        : t(
                                                                                              "posCashClose.difference"
                                                                                          )
                                                                                }
                                                                                color={
                                                                                    isBalanced
                                                                                        ? "success"
                                                                                        : "warning"
                                                                                }
                                                                                size="small"
                                                                            />
                                                                        </TableCell>
                                                                    </>
                                                                )}
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Cash count breakdown (optional; drives close payload conversion) */}
                        {shouldShowClosingCountInputs && (
                            <Grid item xs={12}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            color="primary"
                                        >
                                            Cash Count (Closing)
                                        </Typography>

                                        <Grid container spacing={2}>
                                            {shouldShowDenominationInputs &&
                                                allowedCardLikeModes.map(
                                                (mode) => (
                                                    <Grid
                                                        item
                                                        xs={12}
                                                        sm={6}
                                                        md={3}
                                                        key={mode}
                                                    >
                                                        <TextField
                                                            label={mode}
                                                            type="number"
                                                            fullWidth
                                                            size="small"
                                                            value={
                                                                closingDenomination
                                                                    ?.cardLikeByMode?.[
                                                                    mode
                                                                ] ?? ""
                                                            }
                                                            onChange={(e) =>
                                                                setClosingDenomination(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        cardLikeByMode:
                                                                            {
                                                                                ...(prev?.cardLikeByMode ||
                                                                                    {}),
                                                                                [mode]: e
                                                                                    .target
                                                                                    .value,
                                                                            },
                                                                    })
                                                                )
                                                            }
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                            display="block"
                                                            sx={{ mt: 0.5 }}
                                                        >
                                                            Used for Credit Card
                                                            closing on submit
                                                        </Typography>
                                                    </Grid>
                                                )
                                            )}

                                            {foreignCurrencyFields.map(
                                                (currency) => (
                                                    <Grid
                                                        item
                                                        xs={12}
                                                        sm={6}
                                                        md={3}
                                                        key={`foreign_${currency}`}
                                                    >
                                                        <TextField
                                                            label={`${currency}`}
                                                            type="number"
                                                            fullWidth
                                                            size="small"
                                                            value={
                                                                closingDenomination
                                                                    ?.foreign?.[
                                                                    currency
                                                                ] ?? ""
                                                            }
                                                            onChange={(e) =>
                                                                setClosingDenomination(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        foreign:
                                                                            {
                                                                                ...(prev?.foreign ||
                                                                                    {}),
                                                                                [currency]:
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                            },
                                                                    })
                                                                )
                                                            }
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                            display="block"
                                                            sx={{ mt: 0.5 }}
                                                        >
                                                            {(() => {
                                                                const details =
                                                                    getExchangeRateDetails(
                                                                        currency
                                                                    );
                                                                const rate =
                                                                    toNumber(
                                                                        details.exchange_rate
                                                                    );
                                                                const entered =
                                                                    toNumber(
                                                                        closingDenomination
                                                                            ?.foreign?.[
                                                                            currency
                                                                        ]
                                                                    );
                                                                const source =
                                                                    details.rate_source ===
                                                                    "journal_entry_weighted_rate"
                                                                        ? "JE"
                                                                        : details.rate_source ===
                                                                          "live_exchange_rate_fallback"
                                                                        ? "live"
                                                                        : "missing";
                                                                return `Rate (${source}): ${rate.toFixed(
                                                                    4
                                                                )} -> ${(
                                                                    entered *
                                                                    rate
                                                                ).toFixed(2)} ${
                                                                    posProfile?.currency ||
                                                                    "ILS"
                                                                }`;
                                                            })()}
                                                        </Typography>
                                                    </Grid>
                                                )
                                            )}

                                            {shouldShowDenominationInputs && (
                                            <Grid item xs={12} md={6}>
                                                <Box
                                                    display="flex"
                                                    justifyContent="flex-end"
                                                    alignItems="center"
                                                    gap={2}
                                                    sx={{ height: "100%" }}
                                                >
                                                    <Typography variant="subtitle2">
                                                        ILS Total (Cash):
                                                    </Typography>
                                                    <Typography
                                                        variant="h6"
                                                        color="success.main"
                                                    >
                                                        ₪
                                                        {toNumber(
                                                            denominationTotals.ils
                                                        ).toFixed(2)}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            )}
                                        </Grid>

                                        {shouldShowDenominationInputs && (
                                        <Box sx={{ mt: 2 }}>
                                            <TableContainer
                                                component={Paper}
                                                variant="outlined"
                                            >
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>
                                                                <strong>
                                                                    ILS
                                                                    Denomination
                                                                </strong>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <strong>
                                                                    Count
                                                                </strong>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <strong>
                                                                    Subtotal
                                                                </strong>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {ILS_DENOMINATIONS.map(
                                                            (d) => {
                                                                const count =
                                                                    toNumber(
                                                                        closingDenomination
                                                                            ?.ils?.[
                                                                            String(
                                                                                d
                                                                            )
                                                                        ]
                                                                    );
                                                                const subtotal =
                                                                    toNumber(
                                                                        d
                                                                    ) * count;
                                                                return (
                                                                    <TableRow
                                                                        key={`ils_denom_${d}`}
                                                                    >
                                                                        <TableCell>
                                                                            ₪{d}
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            <TextField
                                                                                type="number"
                                                                                size="small"
                                                                                value={
                                                                                    closingDenomination
                                                                                        ?.ils?.[
                                                                                        String(
                                                                                            d
                                                                                        )
                                                                                    ] ||
                                                                                    ""
                                                                                }
                                                                                onChange={(
                                                                                    e
                                                                                ) => {
                                                                                    const v =
                                                                                        e
                                                                                            .target
                                                                                            .value;
                                                                                    setClosingDenomination(
                                                                                        (
                                                                                            prev
                                                                                        ) => ({
                                                                                            ...prev,
                                                                                            ils: {
                                                                                                ...(prev?.ils ||
                                                                                                    {}),
                                                                                                [String(
                                                                                                    d
                                                                                                )]:
                                                                                                    v,
                                                                                            },
                                                                                        })
                                                                                    );
                                                                                }}
                                                                                inputProps={{
                                                                                    min: 0,
                                                                                    step: 1,
                                                                                    style: {
                                                                                        textAlign:
                                                                                            "right",
                                                                                    },
                                                                                }}
                                                                            />
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            ₪
                                                                            {toNumber(
                                                                                subtotal
                                                                            ).toFixed(
                                                                                2
                                                                            )}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            }
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                display="block"
                                                sx={{ mt: 1 }}
                                            >
                                                Denomination values are applied
                                                only in the closing payload on
                                                submit.
                                            </Typography>
                                        </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Taxes */}
                        {shouldDisplayExpectedAmounts && taxes.length > 0 && (
                            <Grid item xs={12}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            color="primary"
                                        >
                                            {t("posCashClose.taxes")}
                                        </Typography>
                                        <TableContainer
                                            component={Paper}
                                            variant="outlined"
                                        >
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>
                                                            <strong>
                                                                {t(
                                                                    "posCashClose.accountHead"
                                                                )}
                                                            </strong>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <strong>
                                                                {t(
                                                                    "posCashClose.rate"
                                                                )}
                                                            </strong>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <strong>
                                                                {t(
                                                                    "posCashClose.taxAmount"
                                                                )}
                                                            </strong>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {taxes.map((t) => (
                                                        <TableRow
                                                            key={`${t.account_head}:${t.rate}`}
                                                        >
                                                            <TableCell>
                                                                {t.account_head}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {toNumber(
                                                                    t.rate
                                                                ).toFixed(2)}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {toNumber(
                                                                    t.amount
                                                                ).toFixed(2)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Warnings */}
                        {shouldShowManualClosingInputs &&
                            shouldDisplayExpectedAmounts &&
                            hasDifferences && (
                            <Grid item xs={12}>
                                <Alert severity="warning">
                                    {t("posCashClose.differencesWarning")}
                                </Alert>
                            </Grid>
                        )}

                        {/* Action Buttons */}
                        <Grid item xs={12}>
                            <Box
                                display="flex"
                                justifyContent="flex-end"
                                gap={2}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                >
                                    {t("common.cancel")}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={
                                        isSubmitting ||
                                        !posProfileClosingEntries?.length
                                    }
                                    startIcon={
                                        isSubmitting ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <CalculateIcon />
                                        )
                                    }
                                    sx={{ minWidth: 140 }}
                                >
                                    {isSubmitting
                                        ? t("posCashClose.closing")
                                        : t("posCashClose.submitClosing")}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default PosCashCloseDialog;
